import { Form, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PageContainer from "@/pages/containers/page-container";
import SearchAutocomplete from "@/pages/components/search-autocomplete";
import SingleSelect from "@/pages/components/single-slect";
import {
  DEFAULT_DISTANCE,
  DEFAULT_NETWORK_TYPE,
  DISTANCE_OPTIONS,
  QueryKeys,
} from "@/constants";
import { Loader2, MapPin, Search } from "lucide-react";
import type { RootState } from "@/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  resetcostEstimatorFilter,
  setDistance,
  setLocation,
  setNetworks,
  setProcedure,
  setSpecialities,
  setTiers,
  setTransformedNetworkTier,
} from "@/store/price transparency/price-transparency-slice";

import {
  useCostEstimatorDefaults,
  useLocationSearch,
  useProcedureSearch,
  useSpecialitiesSearch,
} from "@/hooks/use-cost-estimator";
import useUserInfo from "@/hooks/use-user-info";
import type Procedure from "@/model/procedure";
import type Place from "@/model/place";

import type Distance from "@/model/distance";
import type { OptionType } from "@/types";
import { useUserLocation } from "@/context/location-provider";
import { useEffect } from "react";
import { queryClient } from "@/hooks/use-query-client";
import type { networkConfigItemType } from "@/model/network";
import { useFeatureName } from "@/hooks/use-pageTitle";
import ScreenRouteNames from "@/constants/screen-route-names";

const CostEstimatorNew = () => {
  const location = useLocation();
  const userLocation = useUserLocation();

  // const { currentLocation } = useGeolocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { key, procedure, targetLocation, distance } = useSelector(
    (state: RootState) => state.priceTransparency,
  );
  const procedureSearchQuery = useProcedureSearch<Procedure>(userInfo); //mutableQuery
  const locationSearchQuery = useLocationSearch<Place>(userInfo); //mutableQuery
  const specialitiesQuery = useSpecialitiesSearch(
    userInfo,
    procedure?.code ?? null,
    {
      onSpecialitiesSuccess: (data) => {
        dispatch(setSpecialities(data.default));
      },
    },
  );

  /**
   * Get the tiers and benefit breakdown and cache the data
   */

  const [networksQuery, benefitBreakdownQuery] = useCostEstimatorDefaults(
    userInfo,
    {
      onNetworksSuccess: (data) => {
        const defaultTiers =
          data.tiers?.length === 1
            ? data.tiers
            : data.tiers.filter(
                (tier) => tier.name === DEFAULT_NETWORK_TYPE.TIER,
              );
        dispatch(setTiers(defaultTiers));
        const transformedNetworkTier = Object.values(data.networks).reduce(
          (acc, { networkName, ...rest }) => {
            acc[networkName] = rest;
            return acc;
          },
          {} as Record<string, networkConfigItemType>,
        );
        dispatch(setTransformedNetworkTier(transformedNetworkTier));
        const defaultNetworks = data.networks.filter((network) =>
          defaultTiers.map((tier) => tier.name).includes(network.tier),
        );
        dispatch(setNetworks(defaultNetworks));
      },
      onBenefitBreakdownSuccess: (_data) => {},
    },
  );

  const isErrorDefaults =
    networksQuery.isError ||
    benefitBreakdownQuery.isError ||
    specialitiesQuery.isError;

  const errorDefaults =
    networksQuery.error ||
    benefitBreakdownQuery.error ||
    specialitiesQuery.error;

  const isPendingDefaults =
    networksQuery.isFetching ||
    benefitBreakdownQuery.isFetching ||
    specialitiesQuery.isFetching ||
    procedureSearchQuery.isPending ||
    locationSearchQuery.isPending;

  /**
   * React life cycle
   */

  // useEffect(() => {
  //   dispatch(resetcostEstimatorFilter());
  // }, []);

  useEffect(() => {
    // console.log("1@", location.key, key);
    queryClient.removeQueries({ queryKey: [QueryKeys.PROVIDER_COST_SEARCH] });
    if (location.key !== key) {
      // console.log("1@ New navigation detected");
      const place: Place = {
        description: userLocation.location?.description || "",
        place_id: userLocation.location?.place_id || "",
        reference: "",
        lat: userLocation.location?.lat || 0,
        lng: userLocation.location?.lng || 0,
      };
      dispatch(resetcostEstimatorFilter(place));
    } else {
      // console.log("1@ Back navigation detected");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      Boolean(userLocation.location?.lat) &&
      Boolean(userLocation.location?.lng) &&
      targetLocation === null
    ) {
      const place: Place = {
        description: userLocation.location?.description || "",
        place_id: userLocation.location?.place_id || "",
        reference: "",
        lat: userLocation.location?.lat,
        lng: userLocation.location?.lng,
      };
      dispatch(setLocation(place));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  useEffect(() => {
    if (procedure) {
      specialitiesQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedure]);

  /**
   * Handle search params
   */

  const handleReset = () => {
    queryClient.removeQueries({ queryKey: [QueryKeys.PROVIDER_COST] });
    dispatch(resetcostEstimatorFilter(null));
  };

  const handleSearch = async () => {
    const targetPath = `/${ScreenRouteNames.COST_ESTIMATOR.path}/${ScreenRouteNames.COST_ESTIMATOR_RESULT.path}?procedure=${procedure?.code}`;

    navigate(targetPath, {
      replace: false,
      state: {
        key: location.key,
      },
    });
  };

  return (
    <PageContainer title={useFeatureName()}>
      <Form method="post">
        <div className="mx-auto max-w-2xl space-y-8 p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <SearchAutocomplete<Procedure, { queryString: string }>
                label="Procedure"
                placeholder="e.g Chest X-Ray (71047)"
                placeholderIcon={Search}
                value={procedure?.description}
                queryFunction={procedureSearchQuery}
                formatResult={(result: Procedure, _index: number) => {
                  return (
                    <div key={result.code}>
                      <span className="text-sm font-medium">
                        {result.description}
                      </span>
                      <div className="text-xs text-muted-foreground">
                        Procedure Code: {result.code}
                      </div>
                    </div>
                  );
                }}
                onSelect={(value: Procedure) => {
                  dispatch(setProcedure(value));
                }}
                onClear={() => {
                  dispatch(setProcedure(null));
                }}
                minSearchLength={3}
                debounceTime={200}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <SearchAutocomplete<Place, { queryString: string }>
                  label="Location"
                  placeholder="Enter a location"
                  placeholderIcon={MapPin}
                  value={targetLocation?.description}
                  queryFunction={locationSearchQuery}
                  onSelect={async (value: Place) => {
                    dispatch(setLocation(value));
                  }}
                  formatResult={(result: Place, _index: number) => {
                    return (
                      <div key={result.place_id}>
                        <div className="text-sm font-medium">
                          {result.description}
                        </div>
                        {/* <div className="text-sm font-medium">
                          {`Lat: ${result.lat.toFixed(4)}, Lng: ${result.lng.toFixed(4)}`}
                        </div> */}
                      </div>
                    );
                  }}
                  onClear={() => {
                    dispatch(setLocation(null));
                  }}
                  minSearchLength={2}
                  debounceTime={200}
                />
              </div>
              <div className="space-y-2">
                <Label>Distance</Label>
                <SingleSelect
                  placeholder="Select Distance"
                  data={DISTANCE_OPTIONS}
                  defaultOption={DEFAULT_DISTANCE}
                  selectedOption={distance}
                  readValueFor="displayName"
                  searchEnabled={false}
                  onSelect={(item: OptionType) => {
                    dispatch(setDistance(item as Distance));
                  }}
                />
              </div>
            </div>
          </div>

          {isErrorDefaults && (
            <div className="text-sm text-red-500">
              {errorDefaults?.message || "An error occurred"}
            </div>
          )}

          <footer className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleReset()}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="default"
              className="min-w-24 hover:bg-primary-selected"
              onClick={() => handleSearch()}
              disabled={
                !procedure ||
                !location ||
                !distance ||
                isErrorDefaults ||
                isPendingDefaults
              }
            >
              {isPendingDefaults ? (
                <Loader2 className="animate-spin text-white" />
              ) : (
                "Search"
              )}
            </Button>
          </footer>
        </div>
      </Form>
    </PageContainer>
  );
};

export default CostEstimatorNew;
